import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Descriptions, Tabs, Button, Space, Spin, message, Row, Col, Typography, Empty } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getVendorById, deleteVendor } from '@/api/vendors';
import type { Vendor } from '@/types/resources/vendor';
import { deleteUser } from '@/api/users';
import { useAuth } from '@/contexts/AuthContext';
import ActivityLogList from '@/components/common/ActivityLogList';
import ConfirmDeleteModal from '@/components/modal/ConfirmDeleteModal';
import { VendorModal } from '@/components/features/resources/vendors/VendorModal';
import { UserInfoTab } from '@/components/tab';

const { Title } = Typography;

export default function VendorDetailsPage() {
  const { t } = useTranslation(['vendors', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { firestoreUser } = useAuth();
  
  const state = location.state as { vendor: Vendor } | null;

  // State
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const shouldFetch = !state?.vendor && !!id && !!firestoreUser;

  // Use state data if available, otherwise fetch
  const { data: vendor, isLoading } = useQuery({
    queryKey: ['vendor', id],
    queryFn: () => {
      if (!id || !firestoreUser) return null;
      return getVendorById(id, firestoreUser);
    },
    enabled: shouldFetch
  });

  // Combine state and fetched data
  const displayVendor = state?.vendor || vendor;

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (vendorId: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      
      // Get the vendor data to check if it has a user reference
      const vendorData = displayVendor;
      if (!vendorData) throw new Error('Vendor not found');
      
      // Delete the vendor first
      await deleteVendor(vendorId, firestoreUser);
      
      // If vendor has a user reference, delete the user as well
      if (vendorData.user?.id) {
        await deleteUser(vendorData.user.id, firestoreUser);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      message.success(t('vendors:messages.deleteSuccess'));
      navigate('/resources/vendors');
    },
    onError: (error: Error) => {
      message.error(error.message || t('vendors:messages.deleteError'));
    }
  });

  // Only show loading state if we're actually fetching
  if (isLoading && !state?.vendor) {
    return <Spin size="large" />;
  }

  // Show error only if we tried to fetch and failed
  if ((!displayVendor || !id) && !state?.vendor) {
    message.error(t('vendors:messages.notFound'));
    return <div>{t('vendors:messages.notFound')}</div>;
  }

  if (!displayVendor) {
    return null;
  }

  const handleEdit = () => {
    setIsModalOpen(true);
  };

  const handleModalSuccess = () => {
    setIsModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ['vendor', id] });
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
  };

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (id) {
      deleteMutation.mutate(id);
      setOpenDeleteDialog(false);
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
  };

  const items = [
    {
      key: 'overview',
      label: t('details.title'),
      children: (
        <Card>
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('details.attributes.name')}>
              {displayVendor.name}
            </Descriptions.Item>
            <Descriptions.Item label={t('details.attributes.category')}>
              {t(`vendors:categories.${displayVendor.category}`, displayVendor.category)}
            </Descriptions.Item>
            <Descriptions.Item label={t('details.attributes.status')}>
              {t(`vendors:status.${displayVendor.status}`, displayVendor.status)}
            </Descriptions.Item>
            <Descriptions.Item label={t('details.attributes.contactPerson')}>
              {displayVendor.contactPerson || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('details.attributes.phone')}>
              {displayVendor.phone || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('details.attributes.email')}>
              {displayVendor.email || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('details.attributes.address')}>
              {displayVendor.address || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('details.attributes.notes')}>
              {displayVendor.notes || '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: 'user',
      label: t('vendors:common.userInfo', 'User Info'),
      children: displayVendor?.user?.id ? (
        <UserInfoTab userId={displayVendor.user.id} />
      ) : <Empty description={t('vendors:noUserInfo', 'No user information available')} />,
    },
    {
      key: 'activityLog',
      label: t('vendors:common.activityLog'),
      children: <ActivityLogList activityRecords={displayVendor.activityRecords || []} />,
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/resources/vendors')}>
              {t('common:back')}
            </Button>
            <Title level={3} style={{ margin: 0 }}>{displayVendor.name}</Title>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Button 
              icon={<EditOutlined />}
              onClick={handleEdit}
            >
              {t('actions.edit')}
            </Button>
            <Button 
              danger
              icon={<DeleteOutlined />}
              onClick={handleDeleteClick}
            >
              {t('actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Content */}
      <Tabs items={items} />

      <VendorModal
        open={isModalOpen}
        vendor={displayVendor}
        onSuccess={handleModalSuccess}
        onCancel={handleModalCancel}
      />

      <ConfirmDeleteModal
        open={openDeleteDialog}
        title={t('common:confirmDeleteTitle')}
        description={t('common:confirmDelete')}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}; 